import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toNumber } from 'lodash';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { HookFormTextInput } from '@components/ReactHookFormFields/HookFormTextInput';
import { BODY_MAX_LENGTH, TOPIC_MAX_LENGTH } from '@constants';
import { useIncidentCreation } from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useSauna } from '@hooks/useSauna';
import { DefaultIncidentRequest } from '@models/api';
import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';
import { QUERY_STRINGS } from '@routes/routes.types';
import { t } from '@stores/localization';
import { getDayOfWeek } from '@utils/date.utils';

import { IncidentFormWrapper } from '../IncidentFormWrapper';

type FormProps = {
  title: string;
  description: string;
};

/** A form for sauna related messages.  */
export const SaunaMessageForm = ({
  customTranslations,
}: ParsedEntry<IWidget>) => {
  const [searchParams] = useSearchParams();
  const { t: tForm } = useCustomTranslations(customTranslations);

  // These search parameters are populated if user navigated here from
  // clicking a recommended sauna shift in sauna reservation form
  const dayOfTheWeek = searchParams.get(QUERY_STRINGS.SAUNA_SHIFT_DAY_OF_WEEK);
  const timeRange = searchParams.get(QUERY_STRINGS.SAUNA_SHIFT_TIME_RANGE);
  const saunaId = searchParams.get(QUERY_STRINGS.SAUNA_ID);

  // These get populated if user is inquiring about changing their current sauna shift to another
  const currentShiftDayOfTheWeek = searchParams.get(
    QUERY_STRINGS.CURRENT_SAUNA_SHIFT_DAY_OF_WEEK
  );
  const currentShiftTimeRange = searchParams.get(
    QUERY_STRINGS.CURRENT_SAUNA_SHIFT_TIME_RANGE
  );

  const saunaData = useSauna(saunaId);

  const isInquiringAboutSpecificSauna = !!(
    dayOfTheWeek &&
    timeRange &&
    saunaId
  );

  const isInquiringAboutChangingSaunaShiftToAnotherOne = !!(
    currentShiftDayOfTheWeek && currentShiftTimeRange
  );

  const { create } = useIncidentCreation<DefaultIncidentRequest>({
    postIncident: api.incidents.postGenericIncident,
    classifications: {
      classification: IncidentClassification.FACILITY_RESERVATION,
      subClassification: IncidentSubClassification.SAUNA_RESERVATION,
    },
    messageTitlePrefix: tForm('messageTitlePrefix'),
  });

  const defaultFormValues = (() => {
    if (!isInquiringAboutSpecificSauna) {
      return {
        title: '',
        description: '',
      };
    }

    return {
      title: isInquiringAboutChangingSaunaShiftToAnotherOne
        ? t('sauna.edit_shift_title')
        : t('sauna.reservation'),

      description: isInquiringAboutChangingSaunaShiftToAnotherOne
        ? t('sauna.saunaShiftChangeInquiryInMessageForm', {
            day: getDayOfWeek(toNumber(dayOfTheWeek), 'short'),
            currentDay: getDayOfWeek(
              toNumber(currentShiftDayOfTheWeek),
              'short'
            ),
            currentTimeRange: currentShiftTimeRange,
            timeRange,
            saunaName: saunaData.name,
          })
        : t('sauna.specificSaunaInquiryInMessageForm', {
            day: getDayOfWeek(toNumber(dayOfTheWeek), 'short'),
            timeRange,
            saunaName: saunaData.name,
          }),
    };
  })();

  return (
    <IncidentFormWrapper<FormProps>
      incidentKind="SaunaMessage"
      withFileUpload={false}
      values={defaultFormValues}
      validationSchema={yup.object().shape({
        title: yup.string().required(),
        description: yup.string().required(),
      })}
      onSubmit={(values) =>
        create({ ...values, spaceId: saunaId ?? undefined })
      }
      className="gap-2"
    >
      <HookFormTextInput
        type="text"
        name="title"
        label={{ value: tForm('titleLabel'), type: 'static' }}
        placeholder={tForm('titlePlaceholder')}
        maxLength={TOPIC_MAX_LENGTH}
      />

      <HookFormTextArea
        type="text"
        label={{ value: tForm('descriptionLabel'), type: 'static' }}
        name="description"
        placeholder={tForm('descriptionPlaceholder')}
        message={tForm('descriptionHelper')}
        rows={6}
        maxLength={BODY_MAX_LENGTH}
      />
    </IncidentFormWrapper>
  );
};
